import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";

import { ExpiringLink } from "../../types/expiring_links.d";

interface ExpiringLinkIndexProps {
  expiringLinks: ExpiringLink[];
}

// #STYLE_GUIDE: Index component shows list with delete functionality
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
export default function ExpiringLinkIndex({ expiringLinks }: ExpiringLinkIndexProps) {
  const { loading, makeRequest } = useApiRequest({
    onSuccess: () => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit("/expiring_links");
    },
    onError: (error) => {
      console.error("Failed to delete expiring_link:", error);
      // #TODO: Add proper error handling/toast notifications
    },
  });

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this expiring_link?")) {
      await makeRequest("DELETE", `/api/v1/expiring_links/${id}`);
    }
  };

  const handleView = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/expiring_links/${id}`);
  };

  const handleEdit = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/expiring_links/${id}/edit`);
  };

  const handleCreate = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/expiring_links/new");
  };

  return (
    <div className="space-y-6">
      {/* Header with Create button */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">ExpiringLinks</h1>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          Create New ExpiringLink
        </button>
      </div>

      {/* Table using daisyUI */}
      <div className="overflow-x-auto">
        <table className="table table-zebra">
          <thead>
            <tr>
              <th>ID</th>
              <th>Slug</th>
              <th>Avatar</th>
              <th>Expires at</th>
              <th>Created at</th>
              <th>Updated at</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {expiringLinks.map((expiringLink) => (
              <tr key={expiringLink.id}>
                <td>{expiringLink.id}</td>
                <td>{expiringLink.slug}</td>
                <td>{expiringLink.avatarDisplayName || expiringLink.avatarId || ''}</td>
                <td>{expiringLink.expiresAt}</td>
                <td>{expiringLink.createdAt}</td>
                <td>{expiringLink.updatedAt}</td>
                <td>{new Date(expiringLink.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="join">
                    <button
                      onClick={() => handleView(expiringLink.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(expiringLink.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(expiringLink.id)}
                      className="btn btn-sm btn-error join-item"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {expiringLinks.length === 0 && (
        <div className="text-center py-8">
          <p className="text-base-content/60">No expiring links found.</p>
          <button
            onClick={handleCreate}
            className="btn btn-primary mt-4"
          >
            Create Your First ExpiringLink
          </button>
        </div>
      )}
    </div>
  );
}
